/**
 * In-memory LRU cache for cross-space single-entity reads.
 *
 * Layered cache (memory → IDB → REST):
 *   - Memory: bounded LRU map (MAX_ENTRIES).
 *   - IDB:    services/idb/entityByIdIDB.ts persists across reloads.
 *   - REST:   GET /api/entities?ids={id} populates both layers on miss.
 *
 * Used by `useEntityById(id)` and any code path that needs the full skeleton
 * for a single entity ID without performing a dataspace-wide bulk load.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { entityService } from '../services/entityService';
import { getEntityById, putEntityById } from '../services/idb/entityByIdIDB';
import { createFileLogger } from '../utils/logger';
import type { BaseEntityType } from '../types/entities';

const log = createFileLogger('ENTITY_BY_ID_CACHE');

const MAX_ENTRIES = 1_000;

interface EntityByIdState {
  entries: Map<string, BaseEntityType>;
  inflight: Map<string, Promise<BaseEntityType | null>>;
  bumpVersion: number;
  get: (id: string) => BaseEntityType | undefined;
  fetch: (id: string) => Promise<BaseEntityType | null>;
  put: (entity: BaseEntityType, opts?: { persist?: boolean }) => void;
  remove: (id: string) => void;
  clear: () => void;
}

function lruInsert(map: Map<string, BaseEntityType>, key: string, value: BaseEntityType): Map<string, BaseEntityType> {
  const next = new Map(map);
  next.delete(key);
  next.set(key, value);
  while (next.size > MAX_ENTRIES) {
    const oldestKey = next.keys().next().value as string | undefined;
    if (!oldestKey) break;
    next.delete(oldestKey);
  }
  return next;
}

export const useEntityByIdCache = create<EntityByIdState>((set, getStore) => ({
  entries: new Map(),
  inflight: new Map(),
  bumpVersion: 0,

  get: (id: string) => getStore().entries.get(id),

  put: (entity: BaseEntityType, opts: { persist?: boolean } = {}) => {
    const id = entity?._id;
    if (!id) return;
    set((s) => ({
      entries: lruInsert(s.entries, id, entity),
      bumpVersion: s.bumpVersion + 1,
    }));
    if (opts.persist !== false) {
      putEntityById(entity).catch(() => {});
    }
  },

  remove: (id: string) => {
    set((s) => {
      const next = new Map(s.entries);
      next.delete(id);
      return { entries: next, bumpVersion: s.bumpVersion + 1 };
    });
  },

  clear: () => {
    set({ entries: new Map(), inflight: new Map(), bumpVersion: 0 });
  },

  fetch: async (id: string): Promise<BaseEntityType | null> => {
    if (!id) return null;
    const fromMemory = getStore().entries.get(id);
    if (fromMemory) return fromMemory;

    const existing = getStore().inflight.get(id);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const fromIdb = await getEntityById(id);
        if (fromIdb) {
          set((s) => ({
            entries: lruInsert(s.entries, id, fromIdb),
            bumpVersion: s.bumpVersion + 1,
          }));
          return fromIdb;
        }

        const results = await entityService.queryEntities({ ids: id });
        const entity = (results as BaseEntityType[])[0] ?? null;
        if (entity) {
          set((s) => ({
            entries: lruInsert(s.entries, id, entity),
            bumpVersion: s.bumpVersion + 1,
          }));
          putEntityById(entity).catch(() => {});
        }
        return entity;
      } catch (err) {
        log.warn(`[EntityByIdCache] fetch(${id}) failed`, err);
        return null;
      } finally {
        set((s) => {
          const inflight = new Map(s.inflight);
          inflight.delete(id);
          return { inflight };
        });
      }
    })();

    set((s) => {
      const inflight = new Map(s.inflight);
      inflight.set(id, promise);
      return { inflight };
    });

    return promise;
  },
}));

/**
 * React hook — returns the cached entity for `id`, kicking off a fetch if missing.
 * Returns `undefined` while the fetch is in flight.
 */
export function useEntityById(id: string | null | undefined): BaseEntityType | undefined {
  // Subscribe to entries so consumers re-render when the cache mutates.
  const entry = useEntityByIdCache((s) => (id ? s.entries.get(id) : undefined));

  // Side effects must run inside useEffect — calling fetch during render
  // violates the rules of hooks and triggers double-fetches under React
  // StrictMode. The inflight map dedups concurrent calls.
  const missing = !!id && !entry;
  useEffect(() => {
    if (!missing || !id) return;
    const cacheState = useEntityByIdCache.getState();
    if (cacheState.inflight.has(id)) return;
    cacheState.fetch(id).catch(() => {});
  }, [id, missing]);

  return entry;
}
